import { SetStateAction, useCallback, useState } from 'react';

import { Maybe } from 'types/UtilityTypes';

export type SetThreadScopedState<T> = (
  forThreadID: Maybe<string>,
  next: SetStateAction<T>
) => void;

/**
 * State that belongs to one email thread, e.g. suggestions fetched for it.
 * - Reads return `emptyValue` until something is stored for the current `threadID`, so a
 *   previous thread's data never renders after a switch. No reset effect is needed.
 * - Writers pass the thread they loaded for. An updater function receives the value stored
 *   for that thread, or `emptyValue` if another thread's value is stored.
 * - `emptyValue` must be referentially stable (a module constant or primitive), since reads
 *   return it as-is and effects may depend on it.
 */
export function useThreadScopedState<T>(
  threadID: Maybe<string>,
  emptyValue: T
): [T, SetThreadScopedState<T>] {
  const [stored, setStored] = useState<ThreadScopedValue<T>>({
    threadID: null,
    value: emptyValue,
  });

  const value = stored.threadID === threadID ? stored.value : emptyValue;

  const setForThread = useCallback<SetThreadScopedState<T>>(
    (forThreadID, next) => {
      setStored((prev) => {
        const prevValue =
          prev.threadID === forThreadID ? prev.value : emptyValue;
        const nextValue = next instanceof Function ? next(prevValue) : next;
        if (prev.threadID === forThreadID && prev.value === nextValue) {
          return prev;
        }
        return { threadID: forThreadID, value: nextValue };
      });
    },
    [emptyValue]
  );

  return [value, setForThread];
}

type ThreadScopedValue<T> = {
  threadID: Maybe<string>;
  value: T;
};
